import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CaseFormRepository } from '../../../infrastructure/repositories/case-form.repository';
import { CaseFormDetailEntity } from '../../../domain/entities/case-form-detail.entity';
import { CaseFormFieldType } from '../../../domain/entities/case-form-field.entity';
import { GetCaseFormVersionByIdQuery } from './get-case-form-version-by-id.query';

/**
 * Use case to get a specific case form version by ID
 * Returns the version with all fields and constraints
 */
@Injectable({ providedIn: 'root' })
export class GetCaseFormVersionByIdUseCase {
  private readonly _repository = inject(CaseFormRepository);

  /**
   * Executes the use case
   * @param query - Query with versionId
   * @returns Observable with the case form version detail
   */
  execute(query: GetCaseFormVersionByIdQuery): Observable<CaseFormDetailEntity> {
    return this._repository.getCaseFormVersionDetailByVersionId(query.versionId.toString()).pipe(
      map((dto) => {
        const entity = new CaseFormDetailEntity();
        entity.id = dto.versionId;
        entity.versionId = dto.versionId;
        entity.versionNumber = dto.versionNumber;
        entity.status = dto.status;
        entity.statusName = dto.statusName;
        // Map other required fields with defaults
        entity.name = '';
        entity.displayName = '';
        entity.description = '';
        entity.caseTypeId = dto.formId;
        entity.caseTypeName = '';
        entity.createdAt = new Date();
        entity.updatedAt = new Date();
        entity.createdBy = '';
        entity.updatedBy = '';
        entity.fields = dto.fields.map((field) => ({
          id: field.fieldId,
          name: field.label, // Use label as name fallback
          fieldType: field.fieldType as CaseFormFieldType,
          label: field.label,
          helperText: field.helperText ?? undefined,
          placeholder: field.placeholder ?? undefined,
          isRequired: field.isRequired,
          isVisibleToRequester: field.isVisibleToRequester,
          sensitivityCategory: field.sensitivityCategory ?? undefined,
          visibilityRule: field.visibilityRule ?? undefined,
          isHiddenByDefault: field.isHiddenByDefault,
          isSystem: field.isSystem,
          sortOrder: field.sortOrder,
          constraints: field.constraints.map((c) => ({
            typeConstraintId: '0', // Not available in DTO
            constraintKey: c.constraintKey,
            value: c.value,
            label: c.constraintKey, // Use key as label fallback
            valueType: 'string', // Default value type
            defaultValue: null, // Not available in this DTO
          })),
        }));
        return entity;
      })
    );
  }
}
